import { PspRules } from "@CommonTypes/backend/PspRules";

export const RoutePostMerchflow_merchflowId_FlowUpdate = "/merchflow/:merchflowId/flow-update";

export interface PayloadPostMerchflow_merchflowId_FlowUpdate {
  productsToInsert: ProductToInsert[];
  productsToRemove: ProductToRemove[];
  productsToSwap: ProductToSwap[];
  pspRules: PspRules;
  cdtSource?: FlowUpdateCdtSource;
  /**
   * Opt out of moving the pog's own CDT onto the chosen source. Absent aligns:
   * asking for fresh candidate labels without moving the pog leaves the two
   * halves of one request keyed on different trees.
   */
  preserveExistingPogCdt?: boolean;
}

/**
 * Where the candidate CDT chain comes from.
 *
 * - POG_SNAPSHOT: the chain the pog was generated on, read from the pog's own
 *   recorded cdt_source. Falls back to the tree pinned at creation for a pog
 *   generated before that was recorded. The default, so a payload written
 *   before this field behaves exactly as it did.
 * - STORE_PRODUCT: the raw store_product chain, with no tree overlay.
 * - CATEGORY_TREE: the store's effective tree, resolved as explicit store
 *   assignment then category default. It never resolves a merchflow's own
 *   compressed tree, so it is not "the CDT just finalized in the studio".
 */
export type FlowUpdateCdtSource = "POG_SNAPSHOT" | "STORE_PRODUCT" | "CATEGORY_TREE";

export interface ResponsePostMerchflow_merchflowId_FlowUpdate {
  storePogTriggerId: number;
}

export interface ProductToRemove {
  productCode: string;
  pnlDecision: PnlDecision;
  storeCodes: string[];
}

export interface ProductToInsert {
  productCode: string;
  adjacentProduct: {
    adjacentProductCode: string;
    placeTo: "LEFT" | "RIGHT";
  } | null;
  facings: "AUTO" | number;
  reducibleProductCode: string | null;
  fitStrategy:
    | "KEEP_ALL_FACINGS"
    | "CAN_REDUCE_FACINGS"
    | "CAN_DELETE_RANGED_PRODUCTS"
    | "CAN_DELETE_RANGED_PRODUCTS_INCLUDING_CORE_RANGE";
  pnlDecision: PnlDecision;
  storeCodes: string[];
  canBeSqueezed: boolean;
}

export interface ProductToSwap {
  productCodeToAdd: string | string[];
  productCodeToRemove: string;
  pnlDecision: PnlDecision;
  storeCodes: string[];
  canSqueezeSwapTo: boolean;
  swapFacingsMode: SwapFacingsMode;
}

export type PnlDecision = "DEFAULT" | "MOVEMENT" | "SALES" | "PROFIT";

export type SwapFacingsMode = "MATCH" | "DOS_MOS_BALANCE";
